import { useCallback, useEffect, useRef, useState } from 'react';

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

/**
 * Polls /version.json to detect new deployments.
 *
 * - Records the buildId on first successful fetch
 * - Re-checks every 5 minutes while the tab is visible
 * - Checks immediately when the user returns to the tab
 * - Returns `updateAvailable: true` when the buildId changes
 */
export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const initialBuildId = useRef<string | null>(null);

  const check = useCallback(async () => {
    try {
      const res = await fetch('/version.json', { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const buildId = data?.buildId;
      if (!buildId) return;

      if (initialBuildId.current === null) {
        initialBuildId.current = buildId;
      } else if (buildId !== initialBuildId.current) {
        setUpdateAvailable(true);
      }
    } catch {
      // Network error or dev mode (no version.json) — skip silently
    }
  }, []);

  useEffect(() => {
    check();

    const timer = setInterval(check, POLL_INTERVAL);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [check]);

  const refresh = useCallback(() => {
    window.location.reload();
  }, []);

  return { updateAvailable, refresh };
}
